/**
 * Order ID formatting utilities for context-aware display
 */

import type { FrontendOrderStatus } from './statusMapping';

// Order type display configuration
export interface OrderTypeDisplay {
  icon: string;
  label: string;
  color: string;
}

// Display context for different users
export const DisplayContext = {
  CUSTOMER: 'CUSTOMER' as const,
  VENDOR: 'VENDOR' as const,
  KITCHEN: 'KITCHEN' as const,
  ADMIN: 'ADMIN' as const
};

export type DisplayContextType = typeof DisplayContext[keyof typeof DisplayContext];

// Order type mappings
const ORDER_TYPE_DISPLAYS: Record<string, OrderTypeDisplay> = {
  'D': { icon: '🍽️', label: 'Dine-in', color: 'var(--text-link)' },   // Vibrant Blue
  'T': { icon: '🥡', label: 'Takeaway', color: 'var(--status-ok)' },  // Emerald Green
  'P': { icon: '🥡', label: 'Parcel', color: 'var(--status-ok)' },    // Emerald Green
  'DL': { icon: '🚚', label: 'Delivery', color: 'var(--status-warn)' }, // Amber Orange
  'C': { icon: '🏪', label: 'Counter', color: 'var(--accent-alt)' }    // Vibrant Purple
};

// Pattern to parse order IDs: TYPE-YYYYMMDD-COUNTER
// Counter is one-or-more digits ([0-9]+) — the previous {3} hard-cap was
// silently truncating ids like "P-20260527-1158" to "P-115" once a vendor's
// per-day order count crossed 999. Same class of bug as the backend
// LPAD-truncation that V172 migration fixed.
const ORDER_ID_PATTERN = /([A-Z]+)-([0-9]{8})-([0-9]+)/;

/**
 * Format order ID for customer display (simplest format)
 * Example: D-20251029-001 -> #123
 */
export function formatForCustomer(orderId: string | null | undefined): string {
  if (!orderId) return '';

  const match = orderId.match(ORDER_ID_PATTERN);
  if (match) {
    const counter = parseInt(match[3], 10);
    return `#${counter}`;
  }
  return `#${orderId}`;
}

/**
 * Format order ID for vendor dashboard
 * Example: D-20251029-001 -> #D-123 (Oct 29)
 * @param orderId - The order ID to format
 * @param options - Formatting options: { includeDate?: boolean, excludeToday?: boolean }
 */
export function formatForVendor(
  orderId: string | null | undefined,
  options?: boolean | { includeDate?: boolean; excludeToday?: boolean }
): string {
  if (!orderId) return '';

  // Handle legacy boolean parameter
  let includeDate = true;
  let excludeToday = false;

  if (typeof options === 'boolean') {
    includeDate = options;
  } else if (options) {
    includeDate = options.includeDate ?? true;
    excludeToday = options.excludeToday ?? false;
  }

  const match = orderId.match(ORDER_ID_PATTERN);
  if (match) {
    const type = match[1];
    const dateStr = match[2];
    const counter = parseInt(match[3], 10);

    // If date suffix not needed, return short format
    if (!includeDate) {
      return `#${type}-${counter}`;
    }

    // Parse date (YYYYMMDD)
    const year = dateStr.substring(0, 4);
    const month = dateStr.substring(4, 6);
    const day = dateStr.substring(6, 8);
    const date = new Date(`${year}-${month}-${day}`);

    // Check if today
    const today = new Date();
    const isToday =
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();

    if (isToday) {
      // If excludeToday is true, don't show "(Today)" label
      if (excludeToday) {
        return `#${type}-${counter}`;
      }
      return `#${type}-${counter} (Today)`;
    }

    // Format date as "Oct 29" or "Nov 17"
    const monthName = date.toLocaleDateString('en', { month: 'short' });
    const dayNum = parseInt(day, 10); // Remove leading zero
    return `#${type}-${counter} (${monthName} ${dayNum})`;
  }
  return `#${orderId}`;
}

/**
 * Format order ID for kitchen/KOT display (ultra-short)
 * Example: D-20251029-001 -> D-1
 */
export function formatForKitchen(orderId: string | null | undefined): string {
  if (!orderId) return '';

  const match = orderId.match(ORDER_ID_PATTERN);
  if (match) {
    const type = match[1];
    const counter = parseInt(match[3], 10);
    return `${type}-${counter}`;
  }
  return orderId;
}

/**
 * Get short format with just type and counter
 * Example: D-20251029-001 -> D-123
 */
export function getShortFormat(orderId: string | null | undefined): string {
  if (!orderId) return '';

  const match = orderId.match(ORDER_ID_PATTERN);
  if (match) {
    const type = match[1];
    const counter = parseInt(match[3], 10);
    return `${type}-${counter}`;
  }
  return orderId;
}

/**
 * Get order type display information
 */
export function getOrderTypeDisplay(orderId: string | null | undefined): OrderTypeDisplay {
  if (!orderId) {
    return { icon: '📋', label: 'Order', color: 'var(--text-tertiary)' };
  }

  const match = orderId.match(ORDER_ID_PATTERN);
  if (match) {
    const type = match[1];
    return ORDER_TYPE_DISPLAYS[type] || { icon: '📋', label: 'Order', color: 'var(--text-tertiary)' };
  }
  return { icon: '📋', label: 'Order', color: 'var(--text-tertiary)' };
}

/**
 * Format order ID based on context
 * @param orderId - The order ID to format
 * @param context - The display context (CUSTOMER, VENDOR, KITCHEN, ADMIN)
 * @param options - Additional formatting options
 */
export function formatOrderId(
  orderId: string | null | undefined,
  context: DisplayContextType,
  options?: { includeDate?: boolean; excludeToday?: boolean }
): string {
  switch (context) {
    case 'CUSTOMER':
      return formatForCustomer(orderId);
    case 'VENDOR':
      return formatForVendor(orderId, options);
    case 'KITCHEN':
      return formatForKitchen(orderId);
    case 'ADMIN':
    default:
      return orderId || '';
  }
}

/**
 * Get complete display information for an order
 */
export interface OrderDisplayInfo {
  fullId: string;
  displayId: string;
  shortId: string;
  icon: string;
  typeLabel: string;
  color: string;
}

export function getOrderDisplayInfo(
  orderId: string | null | undefined,
  context: DisplayContextType
): OrderDisplayInfo {
  const fullId = orderId || '';
  const typeDisplay = getOrderTypeDisplay(orderId);

  return {
    fullId,
    displayId: formatOrderId(orderId, context),
    shortId: getShortFormat(orderId),
    icon: typeDisplay.icon,
    typeLabel: typeDisplay.label,
    color: typeDisplay.color
  };
}

/**
 * Format relative time for order display
 */
export function formatRelativeTime(timestamp: string | Date | null | undefined): string {
  if (!timestamp) return '';

  const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);

  if (diffSeconds < 60) {
    return 'Just now';
  } else if (diffMinutes < 60) {
    return `${diffMinutes} min ago`;
  } else if (diffHours < 24) {
    return `${diffHours}h ago`;
  } else {
    return date.toLocaleDateString('en', { month: 'short', day: 'numeric' });
  }
}

/**
 * Get status color for order status
 */
export function getOrderStatusColor(status: FrontendOrderStatus): string {
  switch (status) {
    case 'PENDING':
      return '#F5A623';
    case 'ACCEPTED':
    case 'ACKNOWLEDGED':
      return '#4A90E2';
    case 'PREPARING':
      return '#7ED321';
    case 'READY':
      return '#50E3C2';
    case 'SERVED':
    case 'COMPLETED':
      return '#9013FE';
    case 'CANCELLED':
      return '#D0021B';
    default:
      return '#666666';
  }
}